import { Component, ViewEncapsulation } from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Clientes } from 'app/interfaces/Cliente';
import { Fazendas } from 'app/interfaces/Fazenda';
import { Operacao } from 'app/interfaces/Operacao';
import { MatSelectModule } from '@angular/material/select';
import { LoadingComponent } from 'app/components/loading/loading.component';
import { DialogService } from 'app/services/dialog/dialog.service';
import { finalize } from 'rxjs';
import { OperacaoService } from 'app/services/operacao/operacao.service';
import { Frente, Frentes } from 'app/interfaces/Frente';
import { ClienteService } from 'app/services/cliente/cliente.service';
import { FazendaService } from 'app/services/fazenda/fazenda.service';
import { TranslocoModule } from '@jsverse/transloco';
import { STATUS_OPERACAO_OPTIONS } from 'app/constants/operacao-status.constants';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-operacoes-form',
  standalone: true,
  imports: [
    MatSelectModule,
    MatPaginatorModule,
    ReactiveFormsModule,
    CommonModule,
    LoadingComponent,
    TranslocoModule,

  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './operacoes-form.component.html',
  styleUrl: './operacoes-form.component.css'
})

export class OperacoesFormComponent {
  public operacaoId!: string | null;
  public loading: boolean = false;
  public frentes: Frentes = [];
  public frentesFiltro: Frentes = [];
  public fazendas: Fazendas = [];
  public operacao!: Operacao;
  public clienteId!: number;
  public loadingBotao: boolean = false;
  public fazendaPlaceHolder: string = 'Selecione um cliente';
  public placeholderClientes: string = 'Carregando as usinas...';
  public loadingFrentes: boolean = false;
  public loadingFazendas: boolean = false;
  public formularioOperacao !: FormGroup;
  public botaoDesabilitado: boolean = true;
  public operacaosSelecionadas: string[] = [];
  public clientes!: Clientes;
  public readonly estados = STATUS_OPERACAO_OPTIONS;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private operacaoService: OperacaoService,
    private router: Router,
    private clienteService: ClienteService,
    private fazendaService: FazendaService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.construirForm();
    this.buscarClientes();
    this.atualizarEstadoBotaoSubmit();
    this.obterOperacaoIdDaRota();
  }

  public onSubmit(): void {
    this.loadingBotao = true;
    if (this.operacaoId) {
      this.atualizar();
    } else {
      this.cadastrar();
    }
  }

  public construirForm(): void {
    this.formularioOperacao = this.formBuilder.group({
      vc_service_id: ['', Validators.required],
      fk_cliente: ['', { validators: Validators.required, updateOn: 'change' }],
      en_estado: ['', Validators.required],
      fk_fazenda: ['', Validators.required],
      ids_frentes: ['', Validators.required],
    }, {
      updateOn: 'blur'
    });
  }

  public buscarClientes(): void {
    this.loading = true;
    this.loadingFazendas = true;
    this.loadingFrentes = true;
    this.placeholderClientes = 'Carregando as usinas...';
    this.clienteService.buscarClientesUsuario()
      .subscribe(
        (clientes) => {
          this.clientes = clientes;
          this.placeholderClientes = 'Selecione uma usina...';
          this.loading = false;

          if (!this.operacaoId) {
            this.formularioOperacao.get('fk_cliente')?.setValue(this.clientes[0].id);
            this.buscarItensCliente();
          }
        });
  }

  public validarQuantidadeFrenteSelecionados(): boolean {
    const idsFrente: number[] = this.formularioOperacao.get('ids_frentes')?.value;
    return idsFrente.length == 0;
  }

  public atualizarEstadoBotaoSubmit(): void {
    this.formularioOperacao.valueChanges.subscribe(() => {
      if (this.operacaoId && this.operacao) {
        this.botaoDesabilitado = !this.formularioFoiAlterado() || this.formularioOperacao.invalid || this.validarQuantidadeFrenteSelecionados();
      } else {
        this.botaoDesabilitado = this.formularioOperacao.invalid || !this.formularioOperacao.dirty || this.validarQuantidadeFrenteSelecionados();
      }
    });
  }

  public mapearValoresDoFormulario(): any[] {
    let valorFomatado: number | string;
    return Object.keys(this.formularioOperacao.controls).map(chave => {
      const valor = this.formularioOperacao.controls[chave].value;

      if (chave === 'fk_cliente') {
        valorFomatado = Number(valor);
      } else {
        valorFomatado = valor;
      }
      return typeof valorFomatado === 'string' ? valorFomatado.trim() : valorFomatado;
    });
  }

  public mapearValoresOriginais(): any[] {
    return Object.keys(this.formularioOperacao.controls).map(chave => {
      let valorOriginal = (this.operacao as any)[chave];
      if (chave == 'ids_frentes') {
        valorOriginal = this.operacao.frentes?.map((frente) => frente.sr_id) || []
      }

      if (typeof valorOriginal === 'string') {
        return valorOriginal.trim();
      }

      return valorOriginal;
    });
  }

  public formularioFoiAlterado(): boolean {
    const valoresAtuais: any[] = this.mapearValoresDoFormulario();
    const valoresOriginais: any[] = this.mapearValoresOriginais();
    return valoresAtuais.some((valor, index) => {
      if (Array.isArray(valor)) {
        if (valor.length !== valoresOriginais[index].length) return true;
        return valor.some((valorArray: any, indexArray: any) =>
          valorArray !== valoresOriginais[index][indexArray]
        )
      }
      return valor !== valoresOriginais[index]
    });
  }

  public validarCampoInvalido(campo: AbstractControl | null): boolean | undefined {
    return campo?.invalid && (campo.dirty || campo.touched);
  }

  public filtrarSelect(event: Event): void {
    const elementoDisparador: HTMLInputElement = event.target as HTMLInputElement;
    const valorFormatado: string = this.formataTextoFiltrado(elementoDisparador.value);
    this.frentesFiltro = this.frentes.filter((option: Frente) => {
      if (!option.vc_nome) return false;
      const nomeFormatado = this.formataTextoFiltrado(option.vc_nome);
      return nomeFormatado.includes(valorFormatado);
    });
  }

  private formataTextoFiltrado(texto: string): string {
    return texto.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  public obterOperacaoIdDaRota(): void {
    this.operacaoId = this.route.snapshot.paramMap.get('id');

    if (this.operacaoId) {
      this.loading = true;
      this.buscarOperacao();
      this.formularioOperacao.get('fk_cliente')?.disable();
    }
  }

  public buscarItensCliente(): void {
    this.loadingFazendas = true;
    this.loadingFrentes = true;
    this.clienteId = this.formularioOperacao.get('fk_cliente')?.value;

    this.formularioOperacao.patchValue({
      fk_fazenda: '',
      ids_frentes: '',
    });

    this.fazendas = [];
    this.frentes = [];
    this.frentesFiltro = [];

    this.buscarFrentesDisponiveisCliente();
    this.buscarFazendasDisponiveisCliente();
  }

  public buscarFazendasDisponiveisCliente(): void {
    this.clienteService.buscarFazendasDisponiveis(this.clienteId).subscribe(
      (fazendas) => {
        this.loadingFazendas = false;
        this.fazendas = fazendas;
      });
  }

  public buscarFrentesDisponiveisCliente(): void {
    this.clienteService.buscarFrentesDisponiveis(this.clienteId, this.operacaoId!).subscribe(
      (frentes) => {
        this.loadingFrentes = false;
        this.frentes = frentes;
        this.frentesFiltro = this.frentes;
      });
  }

  public buscarOperacao(): void {
    this.operacaoService.buscar(this.operacaoId!)
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe(
        (operacao) => {
          this.operacao = operacao;
          this.formularioOperacao.patchValue({
            vc_service_id: operacao.vc_service_id,
            fk_cliente: operacao.fk_cliente,
            en_estado: operacao.en_estado,
          });

          this.clienteId = operacao.fk_cliente!;

          this.loadingFazendas = true;
          this.clienteService.buscarFazendasDisponiveis(this.clienteId).subscribe(
            (fazendas) => {
              this.fazendas = fazendas;
              this.loadingFazendas = false;
              this.formularioOperacao.get('fk_fazenda')?.setValue(operacao.fk_fazenda);
            });

          this.loadingFrentes = true;
          this.clienteService.buscarFrentesDisponiveis(this.clienteId, this.operacaoId!).subscribe(
            (frentes) => {
              this.frentes = [...(operacao.frentes ?? []), ...frentes];
              this.frentesFiltro = this.frentes;
              this.loadingFrentes = false;
              this.formularioOperacao.get('ids_frentes')?.setValue(operacao.frentes?.map((frente) => frente.sr_id) || []);
            });
        });
  }

  public cadastrar(): void {
    const operacaoData: Operacao = this.formularioOperacao.value;

    this.operacaoService.cadastrar(operacaoData)
      .pipe(finalize(() => this.loadingBotao = false))
      .subscribe({
        next: () => {
          this.dialogService
            .sucesso(
              this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.createSuccessTitle'),
              this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.createSuccessMessage')
            )
            .then(() => {
              this.router.navigate(['/premoplan/operacoes']);
            });
        },
        error: (e) => {
          if (e.status === 409) {
            this.dialogService.erro(this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.conflictTitle'), e?.error?.erro);
            return;
          }

          this.dialogService.erro(
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.createErrorTitle'),
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.genericErrorMessage')
          );
        }
      });
  }

  public atualizar(): void {
    const operacaoData: Operacao = {
      ...this.formularioOperacao.value,
      fk_cliente: this.formularioOperacao.get('fk_cliente')?.value
    };

    this.operacaoService.atualizar(operacaoData, Number(this.operacaoId))
      .pipe(finalize(() => this.loadingBotao = false))
      .subscribe({
        next: async () => {
          await this.dialogService.sucesso(
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.updateSuccessTitle'),
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.updateSuccessMessage')
          );

          this.router.navigate(['/premoplan/operacoes']);
        },
        error: (e) => {
          if (e.status === 409) {
            this.dialogService.erro(this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.conflictTitle'            ), e?.error?.erro);
            return;
          }

          this.dialogService.erro(
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.updateErrorTitle'),
            this.transloco.translate('COMPONENTS.CADASTRO_OPERACAO.dialogs.genericErrorMessage')
          );
        }
      });
  }

  public cancelar(): void {
    this.router.navigate(['/premoplan/operacoes'])
  }
}
